import { weekdayName } from 'helpers/weekday';
/**
 * The window of the prematch offer a player looks at: "today", "next 3
 * hours", "tomorrow", one named day... Desktop keeps it in LeagueContext, the
 * phone in the URL (?time=), both as the same key.
 */
export type OfferWindow = { from?: Date; to?: Date };

/** Keys the time filter offers: the next hours and today/tomorrow first
 *  (owner, 2026-10-29: 1 and 3 hours before today, no 24 hours), the named
 *  days come after tomorrow. '24h' still reads for links made before. */
export const OFFER_WINDOW_KEYS = [
  '1h',
  '3h',
  'today',
  'tomorrow',
  '3d',
  'all',
] as const;

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const endOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const dayKey = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** The named days after tomorrow, as keys (YYYY-MM-DD), for the filter. */
export const upcomingDayKeys = (count = 5, now = new Date()): string[] =>
  Array.from({ length: count }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() + 2 + i);
    return dayKey(d);
  });

/** From/to for a window key; an unknown key is the whole offer. */
export const offerWindow = (
  key?: string | null,
  now = new Date()
): OfferWindow => {
  const plusHours = (h: number) => new Date(now.getTime() + h * 3600_000);
  switch (key) {
    case 'today':
      return { to: endOfDay(now) };
    case 'tomorrow': {
      const t = new Date(now);
      t.setDate(t.getDate() + 1);
      return { from: startOfDay(t), to: endOfDay(t) };
    }
    case '1h':
      return { to: plusHours(1) };
    case '3h':
      return { to: plusHours(3) };
    case '24h':
      return { to: plusHours(24) };
    case '3d':
      return { to: plusHours(72) };
  }
  const m = String(key ?? '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return { from: startOfDay(d), to: endOfDay(d) };
  }
  return {};
};

/** Weekday and date of a day key, in the player's language ("pet, 09.10."). */
export const dayKeyLabel = (key: string, locale: string) => {
  const [y, mo, d] = key.split('-').map(Number);
  const date = new Date(y, mo - 1, d);
  const weekday = weekdayName(date, locale, 'short');
  return `${weekday}, ${String(d).padStart(2, '0')}.${String(mo).padStart(2, '0')}.`;
};
